import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import { intlShape, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import {
  Form,
  LocationAutocompleteInput,
  FieldSelect,
  FieldRadioButton,
  IconSpinner,
  PrimaryButton,
} from '../../components';

import css from './TopbarSearchForm.css';
import { KEYWORDS_SEARCH, LOCATION_SEARCH } from '../../util/types';
import IconHourGlass from './IconHourGlass';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import config from '../../config';
import { getCurrentOrigin } from '../../util/geolocation';
import { locationBounds } from '../../util/googleMaps';
import { types as sdkTypes } from '../../util/sdkLoader';

const identity = v => v;
const KEY_CODE_ENTER = 13;
const { LatLng, LatLngBounds } = sdkTypes;

class TopbarSearchFormComponent extends Component {
  constructor(props) {
    super(props);
    this.onChange = this.onChange.bind(this);
    this.searchInput = null;
    this.state = {
      searchLocation: false,
    };
    this.handleKeywordEnter = this.handleKeywordEnter.bind(this);
  }

  onChange(location) {
    if (location.selectedPlace) {
      // Note that we use `onSubmit` instead of the conventional
      // `handleSubmit` prop for submitting. We want to autosubmit
      // when a place is selected, and don't require any extra
      // validations for the form.
      this.props.onSubmit({ location, searchType: LOCATION_SEARCH });
      // blur search input to hide software keyboard
      if (this.searchInput) {
        this.searchInput.blur();
      }
    }
  }

  handleKeywordEnter = (e, values) => {
    const { keywords, searchType } = values;

    if (e.keyCode === KEY_CODE_ENTER && keywords && keywords !== '') {
      const { currentSearchParams } = this.props;
      const { bounds } = currentSearchParams || {};
      if (bounds && bounds instanceof LatLngBounds) {
        this.props.onSubmit({
          searchType,
          keywords,
          location: {
            bounds,
          },
        });
        return;
      }

      if (
        typeof window === 'undefined' ||
        (typeof window !== 'undefined' && !navigator.geolocation)
      ) {
        this.props.onSubmit({
          searchType,
          keywords,
          location: config.defaultLocations.SYDNEY,
        });
        return;
      }

      this.setState({
        searchLocation: true,
      });

      getCurrentOrigin(
        pos => {
          let location = config.defaultLocations.SYDNEY;

          if (pos && pos.coords && pos.coords.latitude & pos.coords.longitude) {
            const originLatLng = new LatLng(pos.coords.latitude, pos.coords.longitude);
            const originMaybe = { origin: originLatLng };
            const viewportBounds = locationBounds(originLatLng, 50000);

            location = {
              address: 'Current location',
              ...originMaybe,
              bounds: viewportBounds,
            };
          }

          this.setState({
            searchLocation: false,
          });

          this.props.onSubmit({
            searchType,
            keywords,
            location,
          });
        },
        () => {
          this.setState({
            searchLocation: false,
          });
          this.props.onSubmit({
            searchType,
            keywords,
            location: config.defaultLocations.SYDNEY,
          });
        }
      );
    }
  };

  shouldComponentUpdate = (nextProps, nextState) => {
    return (
      !isEqual(pick(this.props, ['initialValues']), pick(nextProps, ['initialValues'])) ||
      !isEqual(this.state, nextState)
    );
  };

  render() {
    return (
      <FinalForm
        {...this.props}
        render={formRenderProps => {
          const {
            rootClassName,
            className,
            desktopInputRoot,
            intl,
            isMobile,
            values,
          } = formRenderProps;

          const classes = classNames(rootClassName, className);
          const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

          // Allow form submit only when the place has changed
          const preventFormSubmit = e => e.preventDefault();
          const keywordLabel = intl.formatMessage({ id: 'TopbarSearchForm.keywordLabel' });
          const locationLabel = intl.formatMessage({ id: 'TopbarSearchForm.locationLabel' });
          const keywordPlaceHolder = intl.formatMessage({
            id: 'TopbarSearchForm.keywordPlaceHolder',
          });

          const searchType = values && values.searchType ? values.searchType : '';
          const isLocationSearch = searchType === LOCATION_SEARCH;

          return (
            <Form className={classes} onSubmit={preventFormSubmit}>
              <div className={css.searchWrapper}>
                {isMobile ? (
                  <div className={css.searchTypeMobile}>
                    <FieldRadioButton
                      id={KEYWORDS_SEARCH}
                      name="searchType"
                      label={keywordLabel}
                      value={KEYWORDS_SEARCH}
                    />
                    <FieldRadioButton
                      id={LOCATION_SEARCH}
                      name="searchType"
                      label={locationLabel}
                      value={LOCATION_SEARCH}
                      className={css.locationSearch}
                    />
                  </div>
                ) : (
                  <FieldSelect id={`searchType`} name="searchType" className={css.searchType}>
                    <option value={KEYWORDS_SEARCH}>{keywordLabel}</option>
                    <option value={LOCATION_SEARCH}>{locationLabel}</option>
                  </FieldSelect>
                )}

                {!isLocationSearch ? (
                  <Field
                    name="keywords"
                    type="search"
                    placeholder={keywordPlaceHolder}
                    className={isMobile ? css.mobileInput : css.desktopInput}
                    render={fieldRenderProps => {
                      const { input, placeholder, className } = fieldRenderProps;

                      return (
                        <div className={css.searchKeywordWrapper}>
                          <div className={isMobile ? css.mobileIcon : css.desktopIcon}>
                            {this.state.searchLocation ? (
                              <IconSpinner className={css.iconSpinner} />
                            ) : (
                              <IconHourGlass />
                            )}
                          </div>
                          <input
                            {...input}
                            placeholder={placeholder}
                            className={className}
                            autoComplete="off"
                            onKeyUp={e => this.handleKeywordEnter(e, values)}
                          />
                        </div>
                      );
                    }}
                  />
                ) : (
                  <Field
                    name="location"
                    format={identity}
                    render={({ input, meta }) => {
                      const { onChange, ...restInput } = input;

                      // Merge the standard onChange function with custom behaviur. A better solution would
                      // be to use the FormSpy component from Final Form and pass this.onChange to the
                      // onChange prop but that breaks due to insufficient subscription handling.
                      // See: https://github.com/final-form/react-final-form/issues/159
                      const searchOnChange = value => {
                        onChange(value);
                        this.onChange(value);
                      };

                      const searchInput = { ...restInput, onChange: searchOnChange };
                      return (
                        <LocationAutocompleteInput
                          className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
                          iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
                          inputClassName={isMobile ? css.mobileInput : css.desktopInput}
                          predictionsClassName={
                            isMobile ? css.mobilePredictions : css.desktopPredictions
                          }
                          predictionsAttributionClassName={
                            isMobile ? css.mobilePredictionsAttribution : null
                          }
                          placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
                          closeOnBlur={!isMobile}
                          inputRef={node => {
                            this.searchInput = node;
                          }}
                          input={searchInput}
                          meta={meta}
                        />
                      );
                    }}
                  />
                )}
                {isMobile ? (
                  <PrimaryButton
                    className={css.buttonSearchInMobile}
                    onClick={() => this.handleKeywordEnter({ keyCode: KEY_CODE_ENTER }, values)}
                  >
                    Search
                  </PrimaryButton>
                ) : null}
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

const { func, string, bool } = PropTypes;

TopbarSearchFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  desktopInputRoot: null,
  isMobile: false,
};

TopbarSearchFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  desktopInputRoot: string,
  onSubmit: func.isRequired,
  isMobile: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const TopbarSearchForm = injectIntl(TopbarSearchFormComponent);

export default TopbarSearchForm;
